import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp, getDocs, doc, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import AutoCompleteInput from '../../components/AutocompleteInput';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { EllipsisVerticalIcon, CheckIcon } from '@heroicons/react/20/solid';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';

function calculatePrice(distanceKm) {
  const basePrice = 10; // Prezzo base
  const pricePerKm = 1.5; // Prezzo al km
  return basePrice + distanceKm * pricePerKm;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');

  // used to show warning due to user not verified yet
  const [userVerified, setUserVerified] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [datetime, setDatetime] = useState('');

  const [state, setState] = useState({
    selectedItem: {
      origin: '',
      destination: '',
    },
    open: false,
  });

  const [entries, setEntries] = useState([]);

  useEffect(() => {
    setLoading(() => true);

    const fetchEntries = async uid => {
      try {
        const snapshotRoutes = await getDocs(collection(db, 'routes'));
        const locationsSet = new Set();
        snapshotRoutes.forEach(doc => {
          const data = doc.data();
          if (data.origin) locationsSet.add(data.origin);
          if (data.destination) locationsSet.add(data.destination);
        });

        setLocations(Array.from(locationsSet));

        const entriesRef = collection(db, 'quotes', uid, 'entries');
        const snapshotEntries = await getDocs(entriesRef);
        const entries = snapshotEntries.docs.map(doc => {
          const data = doc.data();

          return {
            id: doc.id,
            origin: data.origin,
            destination: data.destination,
            price: data.price,
            confirmed: data.confirmed,
          };
        });

        setEntries(entries);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(() => false);
      }
    };
    setUserVerified(user.emailVerified);
    fetchEntries(user.uid);
  }, [user]);

  const [distance, setDistance] = useState(null);
  const [price, setPrice] = useState(null);
  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setDistance(null);
    setPrice(null);
    setLoading(() => true);

    try {
      const res = await fetch('/.netlify/functions/get-distance-km', {
        method: 'POST',
        headers: { 'x-api-key': 'abcDEF123456789!@#' },
        body: JSON.stringify({ origin, destination }),
      });

      const data = await res.json();

      setDistance(() => data.distance.value / 1000);
      const calculatedPrice = calculatePrice(data.distance.value / 1000).toFixed(2);
      setPrice(() => calculatedPrice);

      const docRef = await addDoc(collection(db, 'quotes', user.uid, 'entries'), {
        origin: data.origin,
        destination: data.destination,
        distance: data.distance,
        duration: data.duration,
        price: calculatedPrice,
        createdAt: serverTimestamp(),
        confirmed: false,
        payed: false,
        basePrice: 10,
        pricePerKm: 1.5,
        datetime: datetime,
        userId: user.uid,
      });

      setEntries(entries => {
        return [
          ...entries,
          {
            id: docRef.id,
            origin: data.origin,
            destination: data.destination,
            price: calculatedPrice,
          },
        ];
      });
    } catch (err) {
      console.error(err);
      setError('Errore nel calcolo della distanza. Verifica gli indirizzi.');
    } finally {
      setLoading(() => false);
    }
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="text-center">
            <svg
              className="mx-auto mb-4 size-10 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 000 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
              />
            </svg>
            <p className="text-lg font-semibold text-white">Caricamento in corso...</p>
          </div>
        </div>
      )}
      {!userVerified && (
        <div className="mx-auto mt-6 max-w-5xl rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <div className="shrink-0">
              <CheckCircleIcon aria-hidden="true" className="size-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Account non verificato</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Ti chiediamo gentilmente di verificare il proprio account tramite la propria
                  e-mail, grazie.
                </p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    type="button"
                    className="rounded-md bg-yellow-50 px-2 py-1.5 text-sm font-medium text-yellow-800 hover:bg-yellow-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
                  >
                    Invia e-mail di verifica
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserVerified(prev => !prev)}
                    className="ml-3 rounded-md bg-yellow-50 px-2 py-1.5 text-sm font-medium text-yellow-800 hover:bg-yellow-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog
        open={state.open}
        onClose={() => setState(state => ({ ...state, open: false }))}
        className="relative z-10"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div>
                <h2 className="text-base/7 font-semibold text-gray-900">Modify</h2>
                <p className="mt-1 max-w-2xl text-sm/6 text-gray-600">modify the trip</p>

                <div className="mt-10 space-y-8 border-b border-gray-900/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-gray-900/10 sm:border-t sm:border-t-gray-900/10 sm:pb-0">
                  <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                    <label
                      htmlFor="origin-transfer"
                      className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5"
                    >
                      Origin
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <input
                        id="origin-transfer"
                        name="origin"
                        type="text"
                        disabled
                        defaultValue={state.selectedItem.origin}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:max-w-xs sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                    <label
                      htmlFor="destination-transfer"
                      className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5"
                    >
                      Destination
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <input
                        id="destination-transfer"
                        name="destination"
                        type="text"
                        disabled
                        defaultValue={state.selectedItem.destination}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:max-w-xs sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                    <label
                      htmlFor="passengers-number"
                      className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5"
                    >
                      Passengers
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <input
                        id="passengers-number"
                        name="passengers"
                        type="number"
                        value={state.selectedItem.passengers}
                        onChange={e =>
                          setState(state => ({
                            ...state,
                            selectedItem: {
                              ...state.selectedItem,
                              [e.target.name]: e.target.value,
                            },
                          }))
                        }
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:max-w-xs sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                    <label
                      htmlFor="bagadges-number"
                      className="block text-sm/6 font-medium text-gray-900 sm:pt-1.5"
                    >
                      Bagadges
                    </label>
                    <div className="mt-2 sm:col-span-2 sm:mt-0">
                      <input
                        id="bagadges-number"
                        name="bagadges"
                        type="number"
                        value={state.selectedItem.bagadges}
                        onChange={e =>
                          setState(state => ({
                            ...state,
                            selectedItem: {
                              ...state.selectedItem,
                              [e.target.name]: e.target.value,
                            },
                          }))
                        }
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-primary sm:max-w-xs sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setEntries(entries => {
                      return entries.map(entry => {
                        if (entry.id === state.selectedItem.id) {
                          return state.selectedItem;
                        } else {
                          return entry;
                        }
                      });
                    });
                    setState(state => {
                      return { ...state, open: false };
                    });
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:col-start-2"
                >
                  Confirm
                </button>
                <button
                  type="button"
                  data-autofocus
                  onClick={() => setState(state => ({ ...state, open: false }))}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <Dialog open={openConfirmDialog} onClose={setOpenConfirmDialog} className="relative z-10">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div>
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon aria-hidden="true" className="size-6 text-green-600" />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                  <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                    Conferma
                  </DialogTitle>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Confermo la mia richiesta di preventivo.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const entryRef = doc(
                        db,
                        'quotes',
                        user.uid,
                        'entries',
                        state.selectedItem.id
                      );
                      await updateDoc(entryRef, {
                        confirmed: true,
                        updatedAt: serverTimestamp(),
                      });
                    } catch (error) {
                      console.error(error);
                    } finally {
                      setOpenConfirmDialog(false);
                    }
                  }}
                  className="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:col-start-2"
                >
                  Conferma
                </button>
                <button
                  type="button"
                  data-autofocus
                  onClick={() => setOpenConfirmDialog(false)}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                >
                  Chiudi
                </button>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <div className="mx-auto flex max-w-7xl flex-col px-4 py-8 lg:flex-row lg:space-x-6">
        {/* Colonna Destra (Sidebar) - mobile: in alto, desktop: 1/3 sticky a destra */}
        <aside className="order-1 mb-6 w-full lg:order-2 lg:mb-0 lg:w-1/3">
          <div className="sticky top-6 p-4">
            {/* Contenuto della sidebar */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="text-2xl font-bold text-gray-800">Richiedi un preventivo</h2>

              <AutoCompleteInput
                label="Partenza"
                name="origin"
                placeholder="Inserisci la partenza"
                value={origin}
                onChange={setOrigin}
                options={locations}
              />

              <div>
                <label htmlFor="datetime" className="block text-sm font-medium text-gray-700">
                  Giorno e ora
                </label>
                <input
                  type="datetime-local"
                  name="datetime"
                  value={datetime}
                  onChange={e => setDatetime(e.target.value)}
                  id="datetime"
                  className="w-full rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-primary focus:ring-primary"
                  required
                />
              </div>

              <AutoCompleteInput
                label="Destinazione"
                name="destination"
                placeholder="Inserisci la destinazione"
                value={destination}
                onChange={setDestination}
                options={locations}
              />

              <button
                type="submit"
                className="w-full rounded bg-primary py-2 text-white hover:bg-primary"
              >
                Calcola Preventivo
              </button>
            </form>

            {error && <p className="mt-4 text-red-500">{error}</p>}

            {distance !== null && false && (
              <div className="mt-6 rounded bg-gray-100 p-4">
                <p>
                  <strong>Distanza:</strong> {distance.toFixed(2)} km
                </p>
                <p>
                  <strong>Prezzo stimato:</strong> € {price.toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </aside>

        {/* Colonna Sinistra (Contenuto Principale) */}
        <div className="order-2 w-full lg:order-1 lg:w-2/3">
          <div className="bg-white p-6">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                  <h1 className="text-base font-semibold text-gray-900">Preventivi</h1>
                  <p className="mt-2 text-sm text-gray-700">
                    Scegli un preventivo dalla lista per confermare la richiesta.
                  </p>
                </div>
                <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                  {false && (
                    <button
                      type="button"
                      className="block rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                      Add user
                    </button>
                  )}
                </div>
              </div>
              <div className="-mx-4 mt-8 sm:-mx-0">
                <table className="min-w-full divide-y divide-gray-300" id="dashboard-table">
                  <thead>
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0"
                      >
                        Partenza
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
                      >
                        Destinazione
                      </th>
                      <th
                        scope="col"
                        className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                      >
                        Prezzo
                      </th>
                      {false && (
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Role
                        </th>
                      )}
                      <th scope="col" className="py-3.5 pl-3 pr-4 sm:pr-0">
                        <span className="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {entries.map(({ id, origin, destination, price, confirmed }) => (
                      <tr key={id} id={id}>
                        <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0">
                          {origin}
                          <dl className="font-normal lg:hidden">
                            <dt className="sr-only">Partenza</dt>
                            <dd className="mt-1 hidden truncate text-gray-700 sm:block">
                              {origin}
                            </dd>
                            <dt className="sr-only sm:hidden">Destinazione</dt>
                            <dd className="mt-1 truncate text-gray-500 sm:hidden">{destination}</dd>
                          </dl>
                        </td>
                        <td className="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell">
                          {destination}
                        </td>
                        <td className="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                          {`${price} EUR`}
                        </td>
                        {/*<td className="px-3 py-4 text-sm text-gray-500">{person.role}</td>*/}
                        <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                          {/* <a
                            href="#dashboard-table"
                            className="text-primary hover:text-indigo-900"
                          >
                            Conferma<span className="sr-only">, </span>
                          </a>*/}
                          <div className="-my-2 ml-3 inline-block text-left">
                            <Menu as="div" className="relative">
                              <MenuButton className="flex items-center rounded-full p-2 text-gray-400 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                                <span className="sr-only">Open options</span>
                                <EllipsisVerticalIcon aria-hidden="true" className="size-5" />
                              </MenuButton>

                              <MenuItems
                                transition
                                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg outline outline-1 outline-black/5 transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                              >
                                <div className="py-1">
                                  <MenuItem>
                                    {/*<a
                                      href="#dashboard-table"
                                      className="flex justify-between px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none dark:text-gray-300 dark:data-[focus]:bg-white/5 dark:data-[focus]:text-white"
                                    >
                                      <span>Edit</span>
                                    </a>*/}
                                    <button
                                      type="button"
                                      disabled={confirmed}
                                      onClick={() => {
                                        setState(state => ({
                                          ...state,
                                          open: true,
                                          selectedItem: entries.find(item => item.id === id),
                                        }));
                                      }}
                                      className="flex w-full justify-between px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                    >
                                      <span>Apri</span>
                                    </button>
                                  </MenuItem>
                                  <MenuItem>
                                    {/*<a
                                      href="#dashboard-table"
                                      className="flex justify-between px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                    >
                                      <span>Edit</span>
                                    </a>*/}
                                    <button
                                      type="button"
                                      disabled={confirmed}
                                      onClick={async () => {
                                        try {
                                          const entryRef = doc(
                                            db,
                                            'quotes',
                                            user.uid,
                                            'entries',
                                            id
                                          );

                                          await updateDoc(entryRef, {
                                            confirmed: true,
                                            confirmedAt: serverTimestamp(),
                                          });
                                        } catch (error) {
                                          console.error(error);
                                        }

                                        //setOpenConfirmDialog(true);
                                        //setSelectedItem(entries.find(item => item.id === id));
                                      }}
                                      className="flex w-full justify-between px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
                                    >
                                      <span>Conferma</span>
                                    </button>
                                  </MenuItem>
                                  {/*<MenuItem>
                                    <a
                                      href="#dashboard-table"
                                      className="flex justify-between px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none dark:text-gray-300 dark:data-[focus]:bg-white/5 dark:data-[focus]:text-white"
                                    >
                                      <span>Duplicate</span>
                                    </a>
                                  </MenuItem>*/}
                                  {/*<MenuItem>
                                    <button
                                      type="button"
                                      className="flex w-full justify-between px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none dark:text-gray-300 dark:data-[focus]:bg-white/5 dark:data-[focus]:text-white"
                                    >
                                      <span>Archive</span>
                                    </button>
                                  </MenuItem>*/}
                                </div>
                              </MenuItems>
                            </Menu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
